import {Component, OnDestroy, OnInit} from '@angular/core';
import {AppConfigService, CustomerSourcingService, InsuranceCategoryFamily, Ticket, TicketType} from '@lifeislife/lifeislife-domain';
import {CustomerContextService} from '../service/customer-context-service';
import {BehaviorSubject, combineLatest, forkJoin, Observable, Subscription, timer} from 'rxjs';
import {debounceTime, delay, filter, map, publishReplay, refCount, switchMap, tap} from 'rxjs/operators';
import {ShellBannerDesc} from '../app-shell/shell-banners/shell-banner-desc';
import {ShellBannerService} from '../service/shell-banner-service';
import {PendingTicketDraftService} from '../service/pending-ticket-draft.service';
import {CustomerInsuranceInformationRequestHelperService} from '../service/customer-insurance-information-request-helper.service';
import {NavigationService} from '../service/navigation-service';
import {PensionProductsPreviewService} from '../pension/pension-products-preview.service';
import {ContactPensionProductsPreviewModel} from '../pension/contact-pension-products-preview-model';
import {CustomerFrontAppSwitches} from '../../domain/customer-front-app-switches';

@Component({
  selector: 'cf-products-route',
  templateUrl: './products-route.component.html',
  styleUrls: ['./products-route.component.scss'],
})
export class ProductsRouteComponent implements OnInit, OnDestroy {

  InsuranceCategoryFamily = InsuranceCategoryFamily;

  sendingInProgress$ = new BehaviorSubject<boolean>(false);
  sourcingRequestSent: boolean;
  sourcingRequestErrored: boolean;

  showDetails = false;
  pendingTicketDraft$: Observable<Ticket | null>;
  editingInsuranceInfoReaquestDraf$: Observable<Ticket | null>;

  pensionModelLoading: boolean;
  pensionProductsModel$: Observable<ContactPensionProductsPreviewModel[]>;
  pensionProductPerContactFeature$: Observable<boolean>;

  private subscription = new Subscription();

  constructor(
    private sourcingService: CustomerSourcingService,
    private navigationService: NavigationService,
    private customerContextService: CustomerContextService,
    private shellBannerService: ShellBannerService,
    private pendingTicketDraftService: PendingTicketDraftService,
    private customerInsuranceInformationRequestHelperService: CustomerInsuranceInformationRequestHelperService,
    private pensionProductModelService: PensionProductsPreviewService,
    private configService: AppConfigService,
  ) {
  }

  ngOnInit(): void {
    this.subscription = new Subscription();

    // Add banner on pending draft
    this.pendingTicketDraft$ = this.pendingTicketDraftService.getPendingDraft$();
    this.editingInsuranceInfoReaquestDraf$ = this.customerInsuranceInformationRequestHelperService.editingTicketDraft$;
    const bannerSubscription = combineLatest([
      this.pendingTicketDraft$, this.editingInsuranceInfoReaquestDraf$,
    ]).pipe(
      debounceTime(200),
      // No popup editing
      filter(r => r[1] == null),
      map(r => r[0]),
    ).subscribe(d => this.onNewPendingTicketDraft(d));
    this.subscription.add(bannerSubscription);


    this.pensionProductPerContactFeature$ = this.configService.geTypedSwitchEnabled$<CustomerFrontAppSwitches>(
      CustomerFrontAppSwitches.pension_breackdown_per_contact, CustomerFrontAppSwitches,
    );


    this.pensionModelLoading = true;
    this.pensionProductsModel$ = this.pensionProductPerContactFeature$.pipe(
      switchMap((perContact) => this.pensionProductModelService.createPensionProductsPreviewModels$(perContact)),
      tap(a => this.pensionModelLoading = false),
      publishReplay(1), refCount(),
    );
  }

  private onNewPendingTicketDraft(ticket: Ticket) {
    if (ticket == null) {
      this.shellBannerService.clearBanner(ShellBannerService.BANNER_ID_INSURANCE_INFO_REQUEST);
      return;
    }

    let bannerKey;
    let resumeCallback: () => void;
    let cancelCallback: () => void;

    if (ticket.ticketType === TicketType.CUSTOMER_INSURANCE_REQUEST) {
      bannerKey = ShellBannerService.BANNER_ID_INSURANCE_INFO_REQUEST;
      resumeCallback = () => this.customerInsuranceInformationRequestHelperService.loadDraft(ticket);
      cancelCallback = () => this.customerInsuranceInformationRequestHelperService.cancelDraft(ticket, false);

    } else {
      console.warn(`unhandled ticket type ${ticket.ticketType}`);
      return;
    }

    const bannerDesc: ShellBannerDesc = {
      key: bannerKey,
      message: `Vous avez une demande en attente: '${ticket.subject}'`,
      actions: [{
        key: 'resume',
        message: `La reprendre`,
        clearMessage: true,
        callback: resumeCallback,
      }, {
        key: 'delete',
        message: `La supprimer`,
        clearMessage: true,
        callback: cancelCallback,
      }],
    };
    this.shellBannerService.addBanner(bannerDesc);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.shellBannerService.clearBanner(ShellBannerService.BANNER_ID_INSURANCE_INFO_REQUEST);
  }


  onNewSourcingRequestClick() {
    this.customerInsuranceInformationRequestHelperService.createDraft$(null)
      .subscribe();
  }

  onMoreClick() {
    const customerRef = this.customerContextService.getActiveCustomerRef();
    const contactRef = this.customerContextService.getActiveContactRef();

    this.sourcingRequestErrored = false;
    this.sourcingRequestSent = false;

    this.sendingInProgress$.next(true);
    this.sourcingService.notifyPotentialCustomerFromCustomerFront$({
      customerRef: customerRef,
      contactRef: contactRef,
    }).subscribe(
      () => this.onSourcingRequetsSent(),
      e => this.onSourcingRequestError(e),
    );
  }

  private onSourcingRequetsSent() {
    this.sendingInProgress$.next(false);
    this.sourcingRequestSent = true;
  }

  private onSourcingRequestError(error: any) {
    this.sendingInProgress$.next(false);
    this.sourcingRequestSent = false;
    this.sourcingRequestErrored = true;
    console.warn(error);
  }

}
